import { SelectionModel } from '@angular/cdk/collections';
import {
	ChangeDetectionStrategy,
	Component,
	DestroyRef,
	effect,
	inject,
	input,
	OnInit,
	output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatTreeModule } from '@angular/material/tree';
import { ILocation } from '@app/domain/types';
import { ICMMSLocation } from '@app/pages/cmms/interfaces/cmms-global.interfaces';
import { IBuilding, IBuildingFloor } from '@app/shared/interfaces/building.interfaces';
import { LocationSelectorService } from '@app/shared/services/location-selector.service';
import { NgxTolgeeModule } from '@tolgee/ngx';
import { debounceTime } from 'rxjs';

@Component({
	selector: 'locations-tree',
	templateUrl: './locations-tree.component.html',
	styleUrl: './locations-tree.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		MatTreeModule,
		MatButtonModule,
		MatIconModule,
		MatCheckboxModule,
		NgxTolgeeModule,
	],
})
export class LocationsTreeComponent implements OnInit {
	private readonly locationSelectorService = inject(LocationSelectorService);
	private readonly destroyRef = inject(DestroyRef);

	selectableLocations = input<IBuilding[]>([]);
	locationChecklistSelection = new SelectionModel<ILocation>(
		true,
		[],
		true,
		(a: { id: string }, b: { id: string }) => a.id === b.id
	);

	locationSelectionChanged = output<ICMMSLocation[]>();

	locationChildrenAccessor = (node: ILocation) => {
		if (node.contentType === 'building') return (node as IBuilding).floor ?? [];
		if (node.contentType === 'floor') return (node as IBuildingFloor).room ?? [];
		return [];
	};
	locationHasChild = (_: number, node: ILocation) => {
		switch (node.contentType) {
			case 'building':
				return !!(node as IBuilding).floor && (node as IBuilding).floor.length > 0;
			case 'floor':
				return (
					!!(node as IBuildingFloor).room && (node as IBuildingFloor).room.length > 0
				);
			default:
				return false;
		}
	};

	constructor() {
		effect(() => {
			const selectionModel = this.locationChecklistSelection;

			const allNodes: ILocation[] = this.mapDescendantsToLocation(
				this.locationSelectorService.selectedLocations()
			);

			selectionModel.clear();
			selectionModel.select(...allNodes);
		});
	}

	ngOnInit(): void {
		this.subscribeToLocationSelection();
	}

	subscribeToLocationSelection() {
		this.locationChecklistSelection.changed
			.pipe(debounceTime(200), takeUntilDestroyed(this.destroyRef))
			.subscribe(() => {
				const selection: ILocation[] = this.locationChecklistSelection.selected;
				if (selection.length) {
					let filteredSelection = [...selection];
					for (const node of selection) {
						if (node.contentType === 'building' || node.contentType === 'floor') {
							const descendants = this.getDescendants(node);

							filteredSelection = filteredSelection.filter(
								location => !descendants.includes(location)
							);
						}
					}
					this.locationSelectionChanged.emit(
						this.ILocationToCmmsLocation(filteredSelection)
					);
					return;
				}
			});
	}

	getDescendants(node: ILocation): ILocation[] {
		const stack: ILocation[] = [node];
		const descendants: ILocation[] = [];

		while (stack.length > 0) {
			const children = this.locationChildrenAccessor(stack.pop());

			if (children.length) {
				descendants.push(...children);
				stack.push(...children);
			}
		}
		return descendants;
	}

	descendantsAllSelected(node: ILocation): boolean {
		const descendants = this.getDescendants(node);
		return descendants.every(child => this.locationChecklistSelection.isSelected(child));
	}
	descendantsPartiallySelected(node: ILocation): boolean {
		const descendants = this.getDescendants(node);
		const selected = descendants.some(child =>
			this.locationChecklistSelection.isSelected(child)
		);
		return selected && !this.descendantsAllSelected(node);
	}

	locationItemSelectionToggle(node: ILocation): void {
		this.locationChecklistSelection.toggle(node);
		const descendants = this.getDescendants(node);
		if (this.locationChecklistSelection.isSelected(node)) {
			this.locationChecklistSelection.select(...descendants);
			return;
		}
		this.locationChecklistSelection.deselect(...descendants);
	}

	private mapDescendantsToLocation(locations: ILocation[]): ILocation[] {
		return locations.flatMap(
			(location): ILocation[] =>
				[location, ...this.getDescendants(location)] as ILocation[]
		);
	}

	private ILocationToCmmsLocation(locations: ILocation[]): ICMMSLocation[] {
		return locations.map(location => ({
			...location,
			locationId: location.id,
			locationType: location.contentType,
			pk: location.id,
		}));
	}
}
